
















































import { Component, Vue as VueDecorator } from "vue-property-decorator";
import { COLOR_BUTTON_GREEN, COLOR_BUTTON } from "@/utils/colors";
import { themedFill, themedFillTextClass } from "@/utils/Theme";
import { 
  getClientId, 
  hasAdministrationCapabilities, 
  storeAdministratorSession, 
  switchToMemberClient,
  getClientName,
  storeSharedUserSession
} from "@/data/repository/ClientRepository";
import Client from "@/data/models/Client";
import { LoginResult } from "./LoginTypes";
import { SnackbarModule } from "@/store/modules/SnackbarModule";
import { signInRefusalMessageKey, toSignInRefusalReason } from "@/utils/SignInRefusal";
import { setActiveUser, signInWithSharedUserOfGroup } from "@/data/repository/UserRepository";
import { auth } from "firebase";
import { ClientModule } from "@/store/modules/ClientModule";
import SearchFieldComponent from "@/components/common/SearchField.vue";

/** Show the search field only once the list is long enough to warrant it. */
const SEARCH_THRESHOLD = 8;

@Component({
  components: {
    "search-field": SearchFieldComponent,
  },
})
export default class ClientOptionsDialogComponent extends VueDecorator {
  isShowing = false;
  isLoading = false;

  resolve?: (value: LoginResult) => void = undefined;
  reject?: (reason: string) => void = undefined;

  clients: Client[] = [];
  calledViaHeader = false;

  searchQuery = "";

  get showSearch() {
    return this.clients.length > SEARCH_THRESHOLD;
  }

  get filteredClients() {
    const query = this.searchQuery.toLowerCase().trim();

    if (query === "") {
      return this.clients;
    }

    return this.clients.filter(
      (client) => (this.getName(client) ?? "").toLowerCase().indexOf(query) >= 0
    );
  }

  onSearchQueryChanged(searchQuery: string) {
    this.searchQuery = searchQuery;
  }

  memberClientIds: string[] = [];
  sharedClients: string[] = [];
  emailAddress: string | null = null;
  userId: string | null = null;

  /** The option's fill for the active theme: the palette green and the accent red in dark mode. */
  getOptionFill(client: Client) {
    const color = this.getOptionColor(client);

    return color ? themedFill(color) : color;
  }

  /** The option's label class: white in light mode; dark on the palette green in dark mode. */
  getOptionTextClass(client: Client) {
    const color = this.getOptionColor(client);

    return color ? themedFillTextClass(color, "white-font") : "white-font";
  }

  getOptionColor(client: Client) {
    const activeClientId = getClientId();

    if (!activeClientId) {
      return false;
    }
    
    return activeClientId === client.id ? COLOR_BUTTON_GREEN : COLOR_BUTTON;
  }

  getName(client: Client) {
    return getClientName(client);
  }

  clearAdministratorUserRelatedCache() {
    localStorage.removeItem('administratorUserId');
    localStorage.removeItem('administratorClientId');
    localStorage.removeItem('administratorRole');
    localStorage.removeItem('administratorSessionUser');
  }

  clearSharedUserRelatedCache() {
    localStorage.removeItem('sharedClients');
    localStorage.removeItem('emailAddress');
    localStorage.removeItem('userId');
  }

  async onClientSelected(
    client: Client
  ) {
    const activeClientId = getClientId();

    if (
      !activeClientId ||
      activeClientId === client.id
    ) {
      return;
    }

    this.isLoading = true;

    if (
      this.sharedClients.includes(client.id) && 
      this.emailAddress &&
      this.userId
    ) {

      const result = await signInWithSharedUserOfGroup(
        this.emailAddress,
        client.id,
        true
      );

      if (result.wasSuccessful && result.token) {
        await auth().signInWithCustomToken(result.token);

        const setUserResult = await setActiveUser(
          this.userId
        );

        this.clearAdministratorUserRelatedCache();
        this.clearSharedUserRelatedCache();
        ClientModule.clear();

        this.isLoading = false;

        if (setUserResult != null) {
          this.isShowing = false;
          this.resolve && this.resolve(setUserResult);
        }
      }

      // #2610066: the client or user switched to is blocked; say why instead of doing nothing.
      const refusal = toSignInRefusalReason(result.reason);
      if (refusal) {
        SnackbarModule.showError({ key: signInRefusalMessageKey(refusal) });
      }

      // TODO: Add error
      this.isLoading = false;
    }
    else {
      if (hasAdministrationCapabilities()) {
        storeAdministratorSession(activeClientId);

        if (this.sharedClients.length > 1) {
          storeSharedUserSession();
        }
      }
      
      const storedUserId = localStorage.getItem('administratorUserId');
      const isSwitchingBackToAdministrator = client.id === localStorage.getItem('administratorClientId');

      const userId = isSwitchingBackToAdministrator && storedUserId
        ? storedUserId
        : undefined;

      const result = await switchToMemberClient(
        client.email,
        this.memberClientIds,
        userId
      );
      
      this.isLoading = false;

      if (result === LoginResult.CLIENT_BLOCKED || result === LoginResult.USER_BLOCKED) {
        // #2610066: the member is blocked or deleted; the dialog stays open on the current client.
        SnackbarModule.showError({
          key: signInRefusalMessageKey(result === LoginResult.USER_BLOCKED ? "USER_BLOCKED" : "CLIENT_BLOCKED"),
        });
      } else if (result != null) {
        if (isSwitchingBackToAdministrator) {

          this.clearAdministratorUserRelatedCache();
          this.clearSharedUserRelatedCache();
        }

        this.isShowing = false;
        this.resolve && this.resolve(result);
      }
    }
  }

  async onBack() {
    this.isShowing = false;
  }

  async show(
    memberClients: Map<string, Client>,
    calledViaHeader: boolean | null = null,
    sharedClients?: string[],
    emailAddress?: string,
    userId?: string,
  ) {
    const cachedSharedClients: string[] = JSON.parse(localStorage.getItem('sharedClients') || '[]');
    const cachedEmailAddress = localStorage.getItem('emailAddress');
    const cachedUserId = localStorage.getItem('userId');

    this.memberClientIds = Array.from(memberClients.keys());
    this.sharedClients = sharedClients ?? cachedSharedClients ?? [];
    this.emailAddress = emailAddress ?? cachedEmailAddress;
    this.userId = cachedUserId ?? userId ?? null;

    if (calledViaHeader) {
      this.calledViaHeader = true;
    }

    this.clients = Array.from(memberClients.values())
      .sort((a: Client, b: Client) => {
        if (a.hasAdministrationCapabilities && !b.hasAdministrationCapabilities) {
          return -1;
        } else if (!a.hasAdministrationCapabilities && b.hasAdministrationCapabilities) {
          return 1;
        }

        const nameA = a.companyName || a.clientName;
        const nameB = b.companyName || b.clientName;
        
        return nameA.localeCompare(nameB);
      });

    this.searchQuery = "";
    this.isShowing = true;

    return new Promise<LoginResult>((resolve, reject) => {
      this.resolve = resolve;
      this.reject = reject;
    });
  }
}
